"use client";

import { useMemo, useState } from "react";
import clsx from "clsx";
import { unpackRows, type PackedRows } from "@/lib/screener/transport";
import type { StageRow } from "@/lib/screener/types";
import { DataTable, type Column } from "./data-table";
import {
  FilterBuilder,
  applyRules,
  type FilterField,
  type FilterRule,
} from "./filter-builder";
import {
  CountBadge,
  McapSelect,
  RsValue,
  SearchBox,
  TickerCell,
} from "./controls";
import {
  calculateScore,
  distinct,
  fmt,
  fmtSignedPct,
  formatMcap,
  matchMcapFilter,
} from "./logic";

type StageView = StageRow & {
  score: number;
  vs_sma10w: number | null;
  vs_sma30w: number | null;
};

const STAGE_CLS: Record<number, string> = {
  1: "border-border bg-surface-2 text-foreground/80",
  2: "border-up/30 bg-up/10 text-up",
  3: "border-amber-500/30 bg-amber-500/10 text-amber-400",
  4: "border-down/30 bg-down/10 text-down",
};
const STAGE_TONE = { 1: "muted", 2: "up", 3: "amber", 4: "down" } as const;

// stockweb: vs SMA % = (close − sma) / sma × 100 when sma > 0.
const vsPct = (price: number | null, sma: number | null) =>
  price != null && sma ? ((price - sma) / sma) * 100 : null;

const pctCls = (v: number | null) =>
  v == null ? "text-muted" : v >= 0 ? "text-up" : "text-down";

export function StageTab({ data }: { data: PackedRows<StageRow> }) {
  const rows = useMemo(() => unpackRows(data), [data]);
  const [search, setSearch] = useState("");
  const [stageFilterVal, setStageFilterVal] = useState("All"); // 'All' | '1'..'4'
  const [mcapFilter, setMcapFilter] = useState("All");
  const [rules, setRules] = useState<FilterRule[]>([]);

  // Derived columns, computed once. Pre-sorted like stockweb's SQL
  // (stage ASC, stage_days DESC, ticker ASC) so ties keep that order.
  const view = useMemo<StageView[]>(
    () =>
      rows
        .map((r) => ({
          ...r,
          score: calculateScore(r),
          vs_sma10w: vsPct(r.close, r.sma_10w),
          vs_sma30w: vsPct(r.close, r.sma_30w),
        }))
        .sort(
          (a, b) =>
            a.stage_status - b.stage_status ||
            (b.stage_days ?? -Infinity) - (a.stage_days ?? -Infinity) ||
            a.ticker.localeCompare(b.ticker),
        ),
    [rows],
  );

  const fields = useMemo<FilterField<StageView>[]>(
    () => [
      {
        id: "score",
        label: "คะแนน (score)",
        kind: "number",
        get: (r) => r.score,
      },
      {
        id: "stage_days",
        label: "Days in stage",
        kind: "number",
        get: (r) => r.stage_days,
      },
      {
        id: "rs_rating",
        label: "RS Rating",
        kind: "number",
        get: (r) => r.rs_rating,
      },
      {
        id: "rs_rating_sector",
        label: "Sector RS",
        kind: "number",
        get: (r) => r.rs_rating_sector,
      },
      {
        id: "rs_rating_industry",
        label: "Industry RS",
        kind: "number",
        get: (r) => r.rs_rating_industry,
      },
      {
        id: "avg_vol_ratio_9d",
        label: "Avg vol ratio 9d",
        kind: "number",
        get: (r) => r.avg_vol_ratio_9d,
      },
      { id: "close", label: "Close", kind: "number", get: (r) => r.close },
      {
        id: "vs_sma10w",
        label: "vs SMA 10w (%)",
        kind: "number",
        get: (r) => r.vs_sma10w,
      },
      {
        id: "vs_sma30w",
        label: "vs SMA 30w (%)",
        kind: "number",
        get: (r) => r.vs_sma30w,
      },
      {
        id: "sma_10w",
        label: "SMA 10w",
        kind: "number",
        get: (r) => r.sma_10w,
      },
      {
        id: "sma_20w",
        label: "SMA 20w",
        kind: "number",
        get: (r) => r.sma_20w,
      },
      {
        id: "sma_30w",
        label: "SMA 30w",
        kind: "number",
        get: (r) => r.sma_30w,
      },
      {
        id: "mcap_b",
        label: "Market cap ($B)",
        kind: "number",
        get: (r) => (r.market_cap == null ? null : r.market_cap / 1e9),
      },
      {
        id: "stage_status",
        label: "Stage",
        kind: "enum",
        options: ["1", "2", "3", "4"],
        get: (r) => r.stage_status,
      },
      {
        id: "prev_stage_status",
        label: "Prev stage",
        kind: "enum",
        options: ["1", "2", "3", "4"],
        get: (r) => r.prev_stage_status,
      },
      {
        id: "rs_trend",
        label: "RS trend",
        kind: "enum",
        options: distinct(
          rows.map((r) => (r.rs_trend == null ? null : String(r.rs_trend))),
        ),
        get: (r) => (r.rs_trend == null ? null : String(r.rs_trend)),
      },
      { id: "ticker", label: "Ticker", kind: "text", get: (r) => r.ticker },
      {
        id: "company_name",
        label: "Company",
        kind: "text",
        get: (r) => r.company_name,
      },
      {
        id: "sector",
        label: "Sector",
        kind: "enum",
        options: distinct(rows.map((r) => r.sector)),
        get: (r) => r.sector,
      },
      {
        id: "industry",
        label: "Industry",
        kind: "text",
        get: (r) => r.industry,
      },
    ],
    [rows],
  );

  // stockweb `filteredStage` (stage select → search → mcap → RS number filters),
  // with the RS / Sector RS / Industry RS filters expressed as builder rules.
  const filtered = useMemo(() => {
    const s = search.trim().toUpperCase();
    return applyRules(view, rules, fields)
      .filter(
        (r) =>
          stageFilterVal === "All" || String(r.stage_status) === stageFilterVal,
      )
      .filter(
        (r) =>
          !s ||
          r.ticker.includes(s) ||
          r.sector?.toUpperCase().includes(s) ||
          r.company_name?.toUpperCase().includes(s),
      )
      .filter((r) => matchMcapFilter(r.market_cap, mcapFilter));
  }, [view, rules, fields, stageFilterVal, search, mcapFilter]);

  const columns: Column<StageView>[] = [
    {
      id: "ticker",
      label: "Ticker",
      pinned: true,
      sortValue: (r) => r.ticker,
      cell: (r) => <TickerCell ticker={r.ticker} />,
    },
    {
      id: "company_name",
      label: "Company",
      cell: (r) => (
        <div
          className="max-w-[160px] truncate text-xs text-muted"
          title={r.company_name ?? undefined}
        >
          {r.company_name ?? "—"}
        </div>
      ),
    },
    {
      id: "stage_status",
      label: "Stage",
      sortValue: (r) => r.stage_status,
      cell: (r) => (
        <span
          className={clsx(
            "rounded border px-2 py-0.5 text-[11px] font-bold",
            STAGE_CLS[r.stage_status],
          )}
        >
          Stage {r.stage_status}
        </span>
      ),
    },
    {
      id: "score",
      label: "คะแนน",
      align: "center",
      sortValue: (r) => r.score,
      cell: (r) => (
        <span
          className={clsx(
            "font-mono font-bold",
            r.score >= 80
              ? "text-up"
              : r.score >= 60
                ? "text-sky-400"
                : r.score >= 40
                  ? "text-amber-400"
                  : "text-muted",
          )}
        >
          {r.score}
        </span>
      ),
    },
    {
      id: "stage_days",
      label: "Days in Stage",
      align: "center",
      sortValue: (r) => r.stage_days,
      cell: (r) => <span className="font-mono">{r.stage_days ?? "—"}</span>,
    },
    {
      id: "prev_stage_status",
      label: "Prev stage",
      align: "center",
      defaultHidden: true,
      sortValue: (r) => r.prev_stage_status,
      cell: (r) => (
        <span className="font-mono text-muted">
          {r.prev_stage_status ?? "—"}
        </span>
      ),
    },
    {
      id: "close",
      label: "Close",
      align: "right",
      sortValue: (r) => r.close,
      cell: (r) => <span className="font-mono">${fmt(r.close)}</span>,
    },
    {
      id: "sma_10w",
      label: "SMA 10w",
      align: "right",
      sortValue: (r) => r.sma_10w,
      cell: (r) => (
        <span className="font-mono text-muted">${fmt(r.sma_10w)}</span>
      ),
    },
    {
      id: "vs_sma10w",
      label: "vs SMA 10w",
      align: "center",
      sortValue: (r) => r.vs_sma10w,
      cell: (r) => (
        <span className={clsx("font-mono text-xs", pctCls(r.vs_sma10w))}>
          {fmtSignedPct(r.vs_sma10w)}
        </span>
      ),
    },
    {
      id: "sma_20w",
      label: "SMA 20w",
      align: "right",
      sortValue: (r) => r.sma_20w,
      cell: (r) => (
        <span className="font-mono text-muted">${fmt(r.sma_20w)}</span>
      ),
    },
    {
      id: "sma_30w",
      label: "SMA 30w",
      align: "right",
      sortValue: (r) => r.sma_30w,
      cell: (r) => (
        <span className="font-mono text-muted">${fmt(r.sma_30w)}</span>
      ),
    },
    {
      id: "vs_sma30w",
      label: "vs SMA 30w",
      align: "center",
      sortValue: (r) => r.vs_sma30w,
      cell: (r) => (
        <span className={clsx("font-mono text-xs", pctCls(r.vs_sma30w))}>
          {fmtSignedPct(r.vs_sma30w)}
        </span>
      ),
    },
    {
      id: "rs_rating",
      label: "RS Rating",
      align: "center",
      sortValue: (r) => r.rs_rating,
      cell: (r) => <RsValue v={r.rs_rating} hi={80} mid={70} />,
    },
    {
      id: "rs_trend",
      label: "RS trend",
      align: "center",
      defaultHidden: true,
      sortValue: (r) => r.rs_trend,
      cell: (r) => (
        <span className="text-xs text-muted">{r.rs_trend ?? "—"}</span>
      ),
    },
    {
      id: "rs_rating_sector",
      label: "Sector RS",
      align: "center",
      sortValue: (r) => r.rs_rating_sector,
      cell: (r) => <RsValue v={r.rs_rating_sector} hi={80} mid={70} />,
    },
    {
      id: "rs_rating_industry",
      label: "Industry RS",
      align: "center",
      sortValue: (r) => r.rs_rating_industry,
      cell: (r) => <RsValue v={r.rs_rating_industry} hi={80} mid={70} />,
    },
    {
      id: "avg_vol_ratio_9d",
      label: "Avg Vol 9d",
      align: "center",
      sortValue: (r) => r.avg_vol_ratio_9d,
      cell: (r) => {
        const v = r.avg_vol_ratio_9d;
        if (v == null) return <span className="text-muted">—</span>;
        return (
          <span
            className={clsx(
              "font-mono",
              v >= 1.5
                ? "font-bold text-up"
                : v >= 1.0
                  ? "text-sky-400"
                  : "text-muted",
            )}
          >
            {v.toFixed(2)}x
          </span>
        );
      },
    },
    {
      id: "sector",
      label: "Sector",
      defaultHidden: true,
      sortValue: (r) => r.sector,
      cell: (r) => <span className="text-xs">{r.sector ?? "—"}</span>,
    },
    {
      id: "industry",
      label: "Industry",
      defaultHidden: true,
      sortValue: (r) => r.industry,
      cell: (r) => (
        <span className="text-xs text-muted">{r.industry ?? "—"}</span>
      ),
    },
    {
      id: "market_cap",
      label: "Market cap",
      align: "right",
      defaultHidden: true,
      sortValue: (r) => r.market_cap,
      cell: (r) => (
        <span className="font-mono text-xs">{formatMcap(r.market_cap)}</span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <FilterBuilder
        fields={fields}
        rules={rules}
        onChange={setRules}
        quickFilters={
          <>
            {([1, 2, 3, 4] as const).map((st) => (
              <CountBadge
                key={st}
                tone={STAGE_TONE[st]}
                active={stageFilterVal === String(st)}
                onClick={() =>
                  setStageFilterVal((cur) =>
                    cur === String(st) ? "All" : String(st),
                  )
                }
              >
                Stage {st}: {rows.filter((r) => r.stage_status === st).length}
              </CountBadge>
            ))}
          </>
        }
        controls={
          <>
            <SearchBox
              value={search}
              onChange={setSearch}
              placeholder="ค้นหา ticker / sector / company…"
            />
            <McapSelect value={mcapFilter} onChange={setMcapFilter} />
          </>
        }
      />

      <DataTable
        rows={filtered}
        columns={columns}
        rowKey={(r) => r.ticker}
        defaultSort={{ id: "stage_days", asc: false }}
        toolbar={
          <span>
            <span className="font-medium text-foreground">
              {filtered.length}
            </span>{" "}
            หุ้น จาก {rows.length}
          </span>
        }
      />
    </div>
  );
}
